import React, { useEffect, useState } from "react";
import classes from './Charting.module.css';
import HeaderNav from '../../Components/Dahboard Header/HeaderNav/HeaderNav';
import { useLocation, useNavigate } from 'react-router-dom';
import searchicon from "../../Assets/searchicon.png";
import filter from "../../Assets/filter.png";
import { Eye, Edit, Trash, Printer } from "lucide-react";
import Footer from "../../Components/Footer/Footer";
import Swal from 'sweetalert2';
import api, { BASE_URL, withCompanyId } from '../../Components/API/Api';
import { useUser } from '../../UserContext';

const Charting = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useUser();
    const [accountsData, setAccountsData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [allAccounts, setAllAccounts] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [typeFilter, setTypeFilter] = useState("All");
    const [statusFilter, setStatusFilter] = useState("All");
    // Modal state for creating a new account - Regular user modal
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [newAccountName, setNewAccountName] = useState("");
    const [newAccountType, setNewAccountType] = useState(""); // will hold id or name depending on API
    const [creating, setCreating] = useState(false);
    const [accountTypes, setAccountTypes] = useState([]);
    const [loadingTypes, setLoadingTypes] = useState(false);
    
    // Modal state for creating a new account - Admin modal
    const [isAdminCreateModalOpen, setIsAdminCreateModalOpen] = useState(false);
    const [adminNewAccountName, setAdminNewAccountName] = useState("");
    const [adminNewAccountType, setAdminNewAccountType] = useState("");
    const [adminCreating, setAdminCreating] = useState(false);
    const [adminAccountTypes, setAdminAccountTypes] = useState([]);
    const [adminLoadingTypes, setAdminLoadingTypes] = useState(false);
    const [industries, setIndustries] = useState([]);
    const [selectedIndustry, setSelectedIndustry] = useState("");
    const [loadingIndustries, setLoadingIndustries] = useState(false);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [uploadAccountType, setUploadAccountType] = useState("");
    const [uploadAccountTypes, setUploadAccountTypes] = useState([]);
    const [uploadLoadingTypes, setUploadLoadingTypes] = useState(false);
    const [selectedUploadFile, setSelectedUploadFile] = useState(null);
    const [uploadingTemplate, setUploadingTemplate] = useState(false);
    const [uploadingAccounts, setUploadingAccounts] = useState(false);
    const [uploadError, setUploadError] = useState("");

    const token = localStorage.getItem("authToken");

    const typeMap = {
        "1": "Assets",
        "2": "Liabilities",
        "3": "Equity",
        "4": "Revenue",
        "5": "Expenses"
    };

    const categoryMap = {
        "1": "Current Assets",
        "2": "Non-current Assets",
        "3": "Current Liabilities",
        "4": "Non-current Liabilities",
        "5": "Equity",
        // "6": "Operating Revenue",
        "7": "Operating Expenses"
        // "7": "Cost of Sales",
        // "8": "Operating Expenses"
    };

    const [serverPage, setServerPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [perPage, setPerPage] = useState(100);
    const [totalItems, setTotalItems] = useState(0);
    const [pageFrom, setPageFrom] = useState(0);
    const [pageTo, setPageTo] = useState(0);

    const fetchAccounts = async (page = 1) => {
        try {
            setLoading(true);
            const response = await api.get('/v1/account/company-accounts', { params: { page } });
            if (response.data.status) {
                const payload = response.data.data || {};
                const accounts = Array.isArray(payload?.data) ? payload.data : [];

                setAccountsData(accounts);
                setAllAccounts(accounts);
                setFilteredData(accounts);

                setServerPage(Number(payload?.current_page ?? page) || 1);
                setLastPage(Number(payload?.last_page ?? 1) || 1);
                setPerPage(Number(payload?.per_page ?? 100) || 100);
                setTotalItems(Number(payload?.total ?? accounts.length) || accounts.length);
                setPageFrom(Number(payload?.from ?? 0) || 0);
                setPageTo(Number(payload?.to ?? accounts.length) || accounts.length);
            }

        } catch (error) {
            console.error("Error fetching chart of accounts:", error.response?.data || error.message);
        } finally {
            setLoading(false);
        }
    };

    // Fetch all accounts for search functionality
    const fetchAllAccounts = async () => {
        try {
            setLoading(true);
            let allData = [];
            let currentPage = 1;
            let hasMorePages = true;

            while (hasMorePages) {
                const response = await api.get('/v1/account/company-accounts', { params: { page: currentPage } });
                if (response.data.status) {
                    const payload = response.data.data || {};
                    const accounts = Array.isArray(payload?.data) ? payload.data : [];
                    allData = [...allData, ...accounts];

                    const lastPageNum = Number(payload?.last_page ?? 1) || 1;
                    hasMorePages = currentPage < lastPageNum;
                    currentPage++;
                } else {
                    hasMorePages = false;
                }
            }

            // Store all accounts for search
            setAllAccounts(allData);
            setTotalItems(allData.length);

            // Set initial pagination values
            setServerPage(1);
            setPerPage(allData.length > 0 ? 100 : 1);
            setLastPage(Math.ceil(allData.length / 100));

        } catch (error) {
            console.error("Error fetching all accounts:", error.response?.data || error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) {
            fetchAllAccounts();
        } else {
            console.error("No auth token found in localStorage");
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    // Filtering logic
    useEffect(() => {
        let data = Array.isArray(allAccounts) ? [...allAccounts] : [];

        if (searchTerm.trim() !== "") {
            data = data.filter(
                item =>
                    item.gl_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    item.gl_code.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // type filter
        if (typeFilter !== "" && typeFilter !== "All") {
            data = data.filter(item => typeMap[item.class_id] === typeFilter);
        }

        // status filter
        if (statusFilter !== "" && statusFilter !== "All") {
            data = data.filter(item =>
                statusFilter === "Active" ? item.status === "1" : item.status === "0"
            );
        }

        setFilteredData(data);
    }, [searchTerm, typeFilter, statusFilter, allAccounts]);

    // Reset to first server page whenever filters or search change (client-side filter on current page)
    useEffect(() => {
        setServerPage(1);
    }, [searchTerm, typeFilter, statusFilter]);

    // Calculate pagination for filtered data
    const itemsPerPage = 50;
    const startIndex = (serverPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = Array.isArray(filteredData) ? filteredData.slice(startIndex, endIndex) : [];
    const totalFilteredPages = Math.ceil(filteredData.length / itemsPerPage);

    // Load account types and industries when regular user create modal opens
    useEffect(() => {
        const fetchAccountTypes = async () => {
            try {
                setLoadingTypes(true);
                const resp = await api.get('/v1/get-sub-categories');
                const raw = resp?.data?.data ?? resp?.data ?? [];
                const list = Array.isArray(raw) ? raw : [];
                setAccountTypes(list);
            } catch (e) {
                console.error('Failed to load account types', e?.response?.data || e?.message);
                setAccountTypes([]);
            } finally {
                setLoadingTypes(false);
            }
        };

        if (isCreateModalOpen) {
            fetchAccountTypes();
        }
    }, [isCreateModalOpen, token]);

    useEffect(() => {
        const fetchUploadAccountTypes = async () => {
            try {
                setUploadLoadingTypes(true);
                const resp = await api.get('/v1/get-sub-categories');
                const raw = resp?.data?.data ?? resp?.data ?? [];
                const list = Array.isArray(raw) ? raw : [];
                setUploadAccountTypes(list);
            } catch (e) {
                console.error('Failed to load upload account types', e?.response?.data || e?.message);
                setUploadAccountTypes([]);
            } finally {
                setUploadLoadingTypes(false);
            }
        };

        const fetchIndustriesForUpload = async () => {
            try {
                setLoadingIndustries(true);
                const res = await api.get('/v1/industries', { params: withCompanyId({}) });
                const items = res?.data?.data || res?.data || [];
                setIndustries(items);
            } catch (err) {
                // ignore fetch errors for industries in upload modal
            } finally {
                setLoadingIndustries(false);
            }
        };

        if (isUploadModalOpen) {
            fetchUploadAccountTypes();
            const isAdmin = user?.email === "admin@brookes.com";
            if (isAdmin) {
                fetchIndustriesForUpload();
            }
        }
    }, [isUploadModalOpen, token]);

    // Load account types and industries when admin create modal opens
    useEffect(() => {
        const fetchAccountTypes = async () => {
            try {
                setAdminLoadingTypes(true);
                const resp = await api.get('/v1/get-sub-categories');
                const raw = resp?.data?.data ?? resp?.data ?? [];
                const list = Array.isArray(raw) ? raw : [];
                setAdminAccountTypes(list);
            } catch (e) {
                console.error('Failed to load account types', e?.response?.data || e?.message);
                setAdminAccountTypes([]);
            } finally {
                setAdminLoadingTypes(false);
            }
        };

        const fetchIndustries = async () => {
            try {
                setLoadingIndustries(true);
                const resp = await api.get('/v1/industry');
                const raw = resp?.data?.data ?? resp?.data ?? [];
                const list = Array.isArray(raw) ? raw : [];
                setIndustries(list);
            } catch (e) {
                console.error('Failed to load industries', e?.response?.data || e?.message);
                setIndustries([]);
            } finally {
                setLoadingIndustries(false);
            }
        };

        if (isAdminCreateModalOpen) {
            fetchAccountTypes();
            fetchIndustries();
        }
    }, [isAdminCreateModalOpen, token]);


    const getTypeClass = (type) => {
        switch (type) {
            case "Assets": return classes.asset;
            case "Liabilities": return classes.liability;
            case "Equity": return classes.equity;
            case "Revenue": return classes.revenue;
            case "Expenses": return classes.expense;
            default: return "";
        }
    };

    const handleCloseUploadModal = () => {
        setIsUploadModalOpen(false);
        setUploadAccountType("");
        setSelectedUploadFile(null);
        setUploadError("");
    };

    const handleUploadFileChange = (event) => {
        const file = event.target.files?.[0] || null;
        if (!file) {
            setSelectedUploadFile(null);
            setUploadError("");
            return;
        }

        if (!file.name.toLowerCase().endsWith('.xlsx')) {
            setSelectedUploadFile(null);
            setUploadError("Please choose a valid .xlsx file.");
            return;
        }

        setSelectedUploadFile(file);
        setUploadError("");
    };

    const handleDownloadTemplate = async () => {
        try {
            setUploadingTemplate(true);
            setUploadError("");
            const response = await api.get('/v1/download-account-excel-template', {
                responseType: 'blob',
                params: withCompanyId({}),
            });

            const contentType = response?.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            const blob = new Blob([response.data], { type: contentType });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'chart-of-accounts-template.xlsx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            Swal.fire({ icon: 'success', title: 'Template downloaded', timer: 1500, showConfirmButton: false });
        } catch (error) {
            const message = error.response?.data?.message || error.message || 'Failed to download template';
            setUploadError(message);
            Swal.fire({ icon: 'error', title: 'Download failed', text: message });
        } finally {
            setUploadingTemplate(false);
        }
    };

    const handleUploadAccounts = async () => {
        if (!uploadAccountType) {
            setUploadError('Please select an account type.');
            return;
        }

        if (!selectedUploadFile) {
            setUploadError('Please choose an .xlsx file to upload.');
            return;
        }

        try {
            setUploadingAccounts(true);
            setUploadError("");

            const formData = new FormData();
            formData.append('account_type', uploadAccountType);
            formData.append('file', selectedUploadFile);
            withCompanyId(formData);

            // If the logged in user is admin, use the industry upload endpoint
            const isAdmin = user?.email === "admin@brookes.com";

            if (isAdmin && !selectedIndustry) {
                setUploadError('Please select an industry before uploading.');
                setUploadingAccounts(false);
                return;
            }

            if (isAdmin) {
                formData.append('industry_id', selectedIndustry);
            }

            const endpoint = isAdmin ? '/v1/industry/upload-account' : '/v1/upload-account';

            await api.post(endpoint, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            Swal.fire({
                icon: 'success',
                title: 'Accounts uploaded',
                text: 'Your chart of accounts file has been submitted successfully.',
                timer: 2000,
                showConfirmButton: false,
            });

            handleCloseUploadModal();
            await fetchAccounts(1);
        } catch (error) {
            const message = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to upload accounts';
            setUploadError(message);
            Swal.fire({ icon: 'error', title: 'Upload failed', text: message });
        } finally {
            setUploadingAccounts(false);
        }
    };

    const pageMap = {
        '/dashboard': 'Dashboard',
        '/transactions': 'Transactions',
        '/accounts': 'Accounting',
        '/reports': 'Reports',
        '/banking': 'Banking',
        '/settings': 'Settings',
    };

    return (
        <div>
            <HeaderNav activePage={pageMap[location.pathname]} />

            <div className={classes.Container}>
                {/* Top Actions */}
                <div className={classes.actions}>
                    <button className={classes.btns}>New Sales Invoice</button>
                    <button className={classes.btns}>New Purchase Invoice</button>
                    <button className={classes.btns}>All Invoices</button>
                    <button className={`${classes.btns} ${classes.greens}`}>Product & Services</button>
                </div>

                {/* Header */}
                <header className={classes.Header}>
                    <h2 className={classes.Title}>Charts of Accounts</h2>
                    <p className={classes.Titles}>
                        Manage your company's account structure and view account balances.
                    </p>
                </header>

                {/* Filter/Action Bar */}
                <div className={classes.headerActions}>
                    <div className={classes.actionsRight}>
                        <div className={classes.inputWrapper}>
                            <img src={searchicon} alt="Search" className={classes.inputIconGreen} />
                            <input type="text" className={classes.searchBar} placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        </div>
                        <div className={classes.inputWrapper}>
                            <select
                                className={classes.searchBars}
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                            >
                                <option value="All">All Types</option>
                                <option value="Assets">Assets</option>
                                <option value="Liabilities">Liabilities</option>
                                <option value="Equity">Equity</option>
                                <option value="Revenue">Revenue</option>
                                <option value="Expenses">Expenses</option>
                            </select>
                        </div>
                        <div className={classes.inputWrapper}>
                            <select
                                className={classes.searchBars}
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="All">All Status</option>
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                            </select>
                            <img src={filter} alt="Filter" className={classes.inputIcon} />
                        </div>

                    </div>
                    <div className={classes.btnfav}>
                        <button
                            className={classes.btnss}
                            onClick={() => navigate('/Chart-ledger')}
                        >
                            <Printer size={16} style={{ marginRight: "8px", marginBottom: "-3px" }} />
                            Print View
                        </button>
                        <button className={classes.btnss} onClick={() => setIsUploadModalOpen(true)}>
                            Upload Accounts
                        </button>
                        <button className={classes.btnss} onClick={() => setIsCreateModalOpen(true)}>
                            + New Accounts
                        </button>
                        {user?.email === "admin@brookes.com" && (
                            <button className={classes.btnss} onClick={() => setIsAdminCreateModalOpen(true)}>
                                + New Accounts (Admin)
                            </button>
                        )}
                    </div>
                </div>

                {/* Table */}
                <div className={classes.tableContaer}>
                    <table className={classes.table}>
                        <thead className={classes.tableHeader}>
                            <tr>
                                <th>Account Code</th>
                                <th>Account Name</th>
                                <th>Type</th>
                                <th>Category</th>
                                <th>Balance</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.map((item, index) => (
                                <tr key={index} className={classes.tableRow}>
                                    <td className={classes.tableCell}>{item.gl_code}</td>
                                    <td className={`${classes.tableCell} ${classes.accountNameCol}`}>
                                        {item.gl_name}
                                    </td>
                                    <td className={classes.tableCell}>
                                        <span className={`${classes.typeBadge} ${getTypeClass(typeMap[item.class_id])}`}>
                                            {typeMap[item.class_id]}
                                        </span>
                                    </td>
                                    <td className={classes.tableCell}>{categoryMap[item.category_id]}</td>
                                    <td className={`${classes.tableCell} ${classes.balanceCol}`}>
                                        {item.balance}
                                    </td>
                                    <td className={classes.tableCell}>
                                        <span className={item.status === "1" ? classes.activeBadge : classes.inactiveBadge}>
                                            {item.status === "1" ? "Active" : "Inactive"}
                                        </span>
                                    </td>
                                    <td className={`${classes.tableCell} ${classes.iconActions}`}>
                                        <Eye size={16} style={{ color: 'rgb(22,163,74)' }} />
                                        <Edit size={16} style={{ margin: '0 8px', color: 'rgb(37, 99, 235)' }} />
                                        <Trash size={16} style={{ color: 'rgb(255, 2, 2)' }} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className={classes.paginationControls}>
                    <button
                        onClick={() => setServerPage(prev => Math.max(prev - 1, 1))}
                        disabled={serverPage === 1 || loading}
                    >
                        Prev
                    </button>

                    {[...Array(totalFilteredPages)].map((_, i) => (
                        <button
                            key={i}
                            className={serverPage === i + 1 ? classes.active : ""}
                            onClick={() => setServerPage(i + 1)}
                            disabled={loading}
                        >
                            {i + 1}
                        </button>
                    ))}

                    <button
                        onClick={() => setServerPage(prev => Math.min(prev + 1, totalFilteredPages))}
                        disabled={serverPage === totalFilteredPages || loading}
                    >
                        Next
                    </button>

                    <span className={classes.pageText}>
                        Displaying {startIndex + 1} - {Math.min(endIndex, filteredData.length)} of {filteredData.length}
                    </span>
                </div>

            </div>
            {/* Create Account Modal - Regular User */}
            {isCreateModalOpen && (
                <div className={classes.modalOverlay} onClick={() => setIsCreateModalOpen(false)}>
                    <div className={classes.modalDialog} onClick={(e) => e.stopPropagation()}>
                        <div className={classes.modalHeader}>
                            <h5 className={classes.modalTitle}>New Account</h5>
                            <button className={classes.modalClose} onClick={() => setIsCreateModalOpen(false)}>&times;</button>
                        </div>
                        <div className={classes.modalBody}>
                            <div className={classes.formGroup}>
                                <label htmlFor="accountName">Account Name</label>
                                <input
                                    id="accountName"
                                    type="text"
                                    className={classes.formControl}
                                    placeholder="e.g. Cash at Bank"
                                    value={newAccountName}
                                    onChange={(e) => setNewAccountName(e.target.value)}
                                />
                            </div>
                            <div className={classes.formGroup}>
                                <label htmlFor="accountType">Account Type</label>
                                <select
                                    id="accountType"
                                    className={classes.formSelect}
                                    value={newAccountType}
                                    onChange={(e) => setNewAccountType(e.target.value)}
                                >
                                    <option value="" disabled>{loadingTypes ? 'Loading types...' : 'Select type'}</option>
                                    {accountTypes.map((t, idx) => {
                                        const value = t?.id ?? t?.value ?? t?.code ?? t?.description ?? t?.label ?? `${idx}`;
                                        const label = t?.description ?? t?.label ?? t?.title ?? t?.displayName ?? String(value);
                                        return (
                                            <option key={value} value={value}>{label}</option>
                                        );
                                    })}
                                </select>
                            </div>
                        </div>
                        <div className={classes.modalFooter}>
                            <button className={classes.modalBtnSecondary} onClick={() => setIsCreateModalOpen(false)}>Cancel</button>
                            <button
                                className={classes.modalBtnPrimary}
                                disabled={creating || !newAccountName.trim() || !newAccountType}
                                onClick={async () => {
                                    try {
                                        setCreating(true);
                                        const endpoint = '/v1/create-account';
                                        const payload = {
                                            account_name: newAccountName.trim(),
                                            account_type: newAccountType,
                                            ...withCompanyId({})
                                        };

                                        await api.post(endpoint, payload);
                                        Swal.fire({ icon: 'success', title: 'Account created', timer: 1500, showConfirmButton: false });
                                        // Refresh list
                                        setNewAccountName("");
                                        setNewAccountType("");
                                        setIsCreateModalOpen(false);
                                        // re-fetch accounts
                                        try {
                                            await fetchAccounts(1);
                                        } catch (err) {
                                            // swallow refetch error silently
                                        }
                                    } catch (error) {
                                        const message = error.response?.data?.message || error.message || 'Failed to create account';
                                        Swal.fire({ icon: 'error', title: 'Error', text: message });
                                    } finally {
                                        setCreating(false);
                                    }
                                }}
                            >
                                {creating ? 'Creating...' : 'Create Chart of Account'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isUploadModalOpen && (
                <div className={classes.modalOverlay} onClick={handleCloseUploadModal}>
                    <div className={classes.modalDialog} onClick={(e) => e.stopPropagation()}>
                        <div className={classes.modalHeader}>
                            <h5 className={classes.modalTitle}>Upload Chart of Accounts</h5>
                            <button className={classes.modalClose} onClick={handleCloseUploadModal}>&times;</button>
                        </div>
                        <div className={classes.modalBody}>
                            <p className={classes.helperText}>
                                Download the Excel template, fill in your account details, and upload the completed file.
                            </p>
                            <div className={classes.formGroup}>
                                <label htmlFor="uploadAccountType">Account Type</label>
                                <select
                                    id="uploadAccountType"
                                    className={classes.formSelect}
                                    value={uploadAccountType}
                                    onChange={(e) => setUploadAccountType(e.target.value)}
                                >
                                    <option value="" disabled>{uploadLoadingTypes ? 'Loading types...' : 'Select type'}</option>
                                    {uploadAccountTypes.map((t, idx) => {
                                        const value = t?.id ?? t?.value ?? t?.code ?? t?.description ?? t?.label ?? `${idx}`;
                                        const label = t?.description ?? t?.label ?? t?.title ?? t?.displayName ?? String(value);
                                        return (
                                            <option key={value} value={value}>{label}</option>
                                        );
                                    })}
                                </select>
                            </div>
                            {user?.email === "admin@brookes.com" && (
                                <div className={classes.formGroup}>
                                    <label htmlFor="uploadIndustrySelect">Industry</label>
                                    <select
                                        id="uploadIndustrySelect"
                                        className={classes.formSelect}
                                        value={selectedIndustry}
                                        onChange={(e) => setSelectedIndustry(e.target.value)}
                                    >
                                        <option value="" disabled>{loadingIndustries ? 'Loading industries...' : 'Select industry'}</option>
                                        {industries.map((industry, idx) => {
                                            const value = industry?.id ?? industry?.value ?? industry?.code ?? `${idx}`;
                                            const label = industry?.name ?? industry?.title ?? industry?.label ?? String(value);
                                            return (
                                                <option key={value} value={value}>{label}</option>
                                            );
                                        })}
                                    </select>
                                </div>
                            )}
                            <div className={classes.formGroup}>
                                <label htmlFor="uploadAccountsFile">Upload Excel File</label>
                                <input
                                    id="uploadAccountsFile"
                                    type="file"
                                    accept=".xlsx"
                                    className={classes.fileInput}
                                    onChange={handleUploadFileChange}
                                />
                                {selectedUploadFile ? (
                                    <p className={classes.fileName}>Selected file: {selectedUploadFile.name}</p>
                                ) : (
                                    <p className={classes.helperText}>Only .xlsx files are accepted.</p>
                                )}
                            </div>
                            {uploadError ? <div className={classes.errorText}>{uploadError}</div> : null}
                        </div>
                        <div className={classes.modalFooter}>
                            <button className={classes.modalBtnSecondary} onClick={handleCloseUploadModal}>Cancel</button>
                            <button className={classes.modalBtnSecondary} onClick={handleDownloadTemplate} disabled={uploadingTemplate}>
                                {uploadingTemplate ? 'Preparing...' : 'Download Template'}
                            </button>
                            <button
                                className={classes.modalBtnPrimary}
                                onClick={handleUploadAccounts}
                                disabled={uploadingAccounts || !uploadAccountType || !selectedUploadFile || (user?.email === "admin@brookes.com" && !selectedIndustry)}
                            >
                                {uploadingAccounts ? 'Uploading...' : 'Upload Accounts'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Account Modal - Admin User */}
            {isAdminCreateModalOpen && (
                <div className={classes.modalOverlay} onClick={() => setIsAdminCreateModalOpen(false)}>
                    <div className={classes.modalDialog} onClick={(e) => e.stopPropagation()}>
                        <div className={classes.modalHeader}>
                            <h5 className={classes.modalTitle}>New Account (Admin)</h5>
                            <button className={classes.modalClose} onClick={() => setIsAdminCreateModalOpen(false)}>&times;</button>
                        </div>
                        <div className={classes.modalBody}>
                            <div className={classes.formGroup}>
                                <label htmlFor="adminIndustrySelect">Industry</label>
                                <select
                                    id="adminIndustrySelect"
                                    className={classes.formSelect}
                                    value={selectedIndustry}
                                    onChange={(e) => setSelectedIndustry(e.target.value)}
                                >
                                    <option value="" disabled>{loadingIndustries ? 'Loading industries...' : 'Select industry'}</option>
                                    {industries.map((industry, idx) => {
                                        const value = industry?.id ?? industry?.value ?? industry?.code ?? `${idx}`;
                                        const label = industry?.name ?? industry?.title ?? industry?.label ?? String(value);
                                        return (
                                            <option key={value} value={value}>{label}</option>
                                        );
                                    })}
                                </select>
                            </div>
                            <div className={classes.formGroup}>
                                <label htmlFor="adminAccountName">Account Name</label>
                                <input
                                    id="adminAccountName"
                                    type="text"
                                    className={classes.formControl}
                                    placeholder="e.g. Cash at Bank"
                                    value={adminNewAccountName}
                                    onChange={(e) => setAdminNewAccountName(e.target.value)}
                                />
                            </div>
                            <div className={classes.formGroup}>
                                <label htmlFor="adminAccountType">Account Type</label>
                                <select
                                    id="adminAccountType"
                                    className={classes.formSelect}
                                    value={adminNewAccountType}
                                    onChange={(e) => setAdminNewAccountType(e.target.value)}
                                >
                                    <option value="" disabled>{adminLoadingTypes ? 'Loading types...' : 'Select type'}</option>
                                    {adminAccountTypes.map((t, idx) => {
                                        const value = t?.id ?? t?.value ?? t?.code ?? t?.description ?? t?.label ?? `${idx}`;
                                        const label = t?.description ?? t?.label ?? t?.title ?? t?.displayName ?? String(value);
                                        return (
                                            <option key={value} value={value}>{label}</option>
                                        );
                                    })}
                                </select>
                            </div>
                        </div>
                        <div className={classes.modalFooter}>
                            <button className={classes.modalBtnSecondary} onClick={() => setIsAdminCreateModalOpen(false)}>Cancel</button>
                            <button
                                className={classes.modalBtnPrimary}
                                disabled={adminCreating || !adminNewAccountName.trim() || !adminNewAccountType || !selectedIndustry}
                                onClick={async () => {
                                    try {
                                        setAdminCreating(true);
                                        const endpoint = '/v1/industry/add-account';
                                        const payload = {
                                            industry_id: selectedIndustry,
                                            account_type: adminNewAccountType,
                                            account_name: adminNewAccountName.trim(),
                                            ...withCompanyId({})
                                        };

                                        await api.post(endpoint, payload);
                                        Swal.fire({ icon: 'success', title: 'Account created', timer: 1500, showConfirmButton: false });
                                        // Refresh list
                                        setAdminNewAccountName("");
                                        setAdminNewAccountType("");
                                        setSelectedIndustry("");
                                        setIsAdminCreateModalOpen(false);
                                        // re-fetch accounts
                                        try {
                                            await fetchAccounts(1);
                                        } catch (err) {
                                            // swallow refetch error silently
                                        }
                                    } catch (error) {
                                        const message = error.response?.data?.message || error.message || 'Failed to create account';
                                        Swal.fire({ icon: 'error', title: 'Error', text: message });
                                    } finally {
                                        setAdminCreating(false);
                                    }
                                }}
                            >
                                {adminCreating ? 'Creating...' : 'Create Chart of Account'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <Footer />
        </div>
    );
};

export default Charting;
